import { supabase } from '../supabase/client.js';

const DOC_TABLE = 'vim_pay_tax_documents';
const ELECTION_TABLE = 'vim_pay_payroll_tax_elections';
const CALC_TABLE = 'vim_pay_payroll_tax_calculations';
const BUCKET = 'vim-pay-tax-documents';
const MAX_BYTES = 10 * 1024 * 1024;

export async function listMyPayrollTaxDocuments(contractorId) {
  if (!supabase || !contractorId) return [];
  const { data, error } = await supabase.from(DOC_TABLE)
    .select('id,contractor_id,document_type,document_year,status,submitted_at,reviewed_at')
    .eq('contractor_id', contractorId)
    .in('document_type', ['w4','a4'])
    .order('submitted_at', { ascending: false });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function uploadPayrollTaxDocument({ userId, contractorId, documentType, taxYear, file }) {
  if (!supabase || !userId || !contractorId) throw new Error('VIM Pay session is unavailable.');
  if (!['w4','a4'].includes(documentType)) throw new Error('Payroll tax document must be W-4 or Arizona A-4.');
  if (!file) throw new Error(`Choose a ${documentType.toUpperCase()} document to upload.`);
  if (Number(file.size || 0) > MAX_BYTES) throw new Error('Tax documents must be 10 MB or smaller.');
  const allowed = new Set(['application/pdf','image/jpeg','image/png']);
  if (file.type && !allowed.has(file.type)) throw new Error('Tax documents must be PDF, JPG, or PNG.');
  const year = Number(taxYear);
  if (!Number.isInteger(year) || year < 2020 || year > 2100) throw new Error('Tax year is invalid.');
  const ext = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/png' ? 'png' : 'pdf';
  const objectPath = `${userId}/${Date.now()}-${documentType}-${year}.${ext}`;
  const { error: uploadError } = await supabase.storage.from(BUCKET)
    .upload(objectPath, file, { cacheControl: '0', upsert: false, contentType: file.type || 'application/pdf' });
  if (uploadError) throw uploadError;
  const { data, error } = await supabase.from(DOC_TABLE)
    .insert({ contractor_id: contractorId, document_type: documentType, document_year: year, object_path: objectPath })
    .select('id,contractor_id,document_type,document_year,status,submitted_at,reviewed_at').single();
  if (error) throw error;
  return data;
}

export async function listMyPayrollTaxElections(contractorId, taxYear) {
  if (!supabase || !contractorId) return [];
  let query = supabase.from(ELECTION_TABLE)
    .select('id,contractor_id,tax_year,revision,is_current,federal_filing_status,federal_step2_checkbox,federal_step3_credits,federal_step4a_other_income,federal_step4b_deductions,federal_step4c_extra_withholding,federal_exempt,arizona_percentage,arizona_extra_withholding,arizona_zero_liability,certification_accepted,certified_at,status,reviewed_at,created_at')
    .eq('contractor_id', contractorId)
    .order('revision', { ascending: false });
  if (taxYear) query = query.eq('tax_year', Number(taxYear));
  const { data, error } = await query;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function listMyPayrollTaxCalculations(contractorId, taxYear) {
  if (!supabase || !contractorId) return [];
  let query = supabase.from(CALC_TABLE)
    .select('id,payment_request_id,worker_id,project_id,assignment_id,payroll_year,pay_frequency,pay_period_start,pay_period_end,pay_date,revision,is_current,gross_wages,employee_social_security,employee_medicare,employee_additional_medicare,federal_income_tax_withheld,arizona_income_tax_withheld,total_employee_taxes,net_pay,federal_default_w4_applied,arizona_default_a4_applied,federal_method_version,arizona_method_version,calculation_status,calculated_at')
    .eq('worker_id', contractorId)
    .eq('is_current', true)
    .order('calculated_at', { ascending: false });
  if (taxYear) query = query.eq('payroll_year', Number(taxYear));
  const { data, error } = await query;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function savePayrollTaxElection(contractorId, values) {
  if (!supabase || !contractorId) throw new Error('VIM Pay session is unavailable.');
  const year = Number(values.tax_year);
  if (!Number.isInteger(year) || year < 2020 || year > 2100) throw new Error('Tax year is invalid.');
  if (!values.certification_accepted) throw new Error('You must certify that these withholding elections match the official forms you submitted.');
  const existing = await listMyPayrollTaxElections(contractorId, year);
  const current = existing.find((item) => item.is_current);
  if (current) {
    const { error } = await supabase.from(ELECTION_TABLE).update({ is_current: false, updated_at: new Date().toISOString() }).eq('id', current.id);
    if (error) throw error;
  }
  const revision = Math.max(0, ...existing.map((item) => Number(item.revision || 0))) + 1;
  const allowedAz = new Set([0,0.005,0.01,0.015,0.02,0.025,0.03,0.035]);
  const azPct = values.arizona_percentage === '' || values.arizona_percentage == null ? null : Number(values.arizona_percentage);
  if (azPct != null && !allowedAz.has(azPct)) throw new Error('Choose a valid Arizona A-4 withholding percentage.');
  const record = {
    contractor_id: contractorId,
    tax_year: year,
    revision,
    is_current: true,
    federal_filing_status: ['single_or_mfs','married_filing_jointly','head_of_household'].includes(values.federal_filing_status) ? values.federal_filing_status : 'single_or_mfs',
    federal_step2_checkbox: Boolean(values.federal_step2_checkbox),
    federal_step3_credits: Math.max(0, Number(values.federal_step3_credits || 0)),
    federal_step4a_other_income: Math.max(0, Number(values.federal_step4a_other_income || 0)),
    federal_step4b_deductions: Math.max(0, Number(values.federal_step4b_deductions || 0)),
    federal_step4c_extra_withholding: Math.max(0, Number(values.federal_step4c_extra_withholding || 0)),
    federal_exempt: Boolean(values.federal_exempt),
    arizona_percentage: azPct,
    arizona_extra_withholding: Math.max(0, Number(values.arizona_extra_withholding || 0)),
    arizona_zero_liability: Boolean(values.arizona_zero_liability),
    certification_accepted: true,
    certified_at: new Date().toISOString(),
  };
  if (record.arizona_zero_liability) record.arizona_percentage = 0;
  const { data, error } = await supabase.from(ELECTION_TABLE).insert(record).select('*').single();
  if (error) throw error;
  return data;
}
